import {
    AddressCategory,
    CollectionServiceType,
    DeliveryServiceType,
    LaneGroupTemperatureRange,
    LaneStatus,
    LeaseType,
    LocationType,
    ProductType,
    TransportModeRoad,
} from 'app/enums/label.enums'
import {
    DTOCardFormField, FieldInCard, NestedField,
} from 'app/types/form.types'
import {
    Country,
} from 'app/Apps/ContactManagement/Countries/Countries.types'
import {
    SizeLink, TypeLink,
} from 'app/shared-components/SkyNetLink'

import {
    TypeIcon,
} from 'app/shared-components/NotificationIcon/notificationIcon.types'
import {
    OpportunityFilters,
} from 'app/shared-components/OpportunitiesSelector/OpportunititesSelector.types'
import {
    AccountLane, AccountLaneProductTypes,
} from '../AccountLanes.types'
import TooltipContentDeliveryService from './AccountLaneTooltipContent/TooltipContentDeliveryService'
import TooltipContentCollectionService from './AccountLaneTooltipContent/TooltipContentCollectionService'

type AccountLaneFormFields = AccountLane & AccountLaneProductTypes

const getFields = ({
    getAllowedValues,
    showFTL,
    showLTL,
    isDeliveryRoad,
    isCollection,
    handoverPointCountry,
    collectionDropoffPointCountry,
    showProductTypes,
    showAwardedProductTypes,
    exists,
    quoteInLink,
    quoteInNumber,
    disabled,
    laneLeadPermission,
    requiredBillingCompany,
    tempRangeDisabled,
    accountDisabled,
    opportunityFilters,
}: {
    getAllowedValues: (val: Record<string, string>) => Record<string, string>,
    showFTL: boolean,
    showLTL: boolean,
    isDeliveryRoad: boolean,
    isCollection: boolean,
    handoverPointCountry?: Country | { id: number },
    collectionDropoffPointCountry?: Country | { id: number },
    showProductTypes?: boolean,
    showAwardedProductTypes?: boolean,
    exists: boolean,
    quoteInLink?: string,
    quoteInNumber?: string,
    disabled?: boolean,
    isAccountAdmin?: boolean,
    laneLeadPermission?: boolean,
    requiredBillingCompany?: boolean,
    tempRangeDisabled?: boolean,
    accountDisabled?: boolean,
    opportunityFilters: OpportunityFilters
}): DTOCardFormField<keyof AccountLaneFormFields>[] => {
    const generalFields: FieldInCard<keyof AccountLaneFormFields>[] = [
        {
            id: 'column1',
            fields: [
                {
                    name: 'laneNumber',
                    componentName: 'InputSingleline',
                    labelKey: 'AccountLane.laneNumber',
                    disabled: true,
                },
                {
                    name: 'account',
                    componentName: 'AccountSelector',
                    labelKey: 'AccountLane.accountCompanyName',
                    required: true,
                    disabled: disabled || accountDisabled,
                },
                ...exists ? [{
                    name: 'contractBasisAccount',
                    componentName: 'AccountSelector',
                    labelKey: 'Prebooking.pricingAccount',
                    disabled: true,
                }] as NestedField<keyof AccountLaneFormFields>[] : [],
                {
                    name: 'temperatureRange',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(LaneGroupTemperatureRange),
                    labelKey: 'AccountLane.temperatureRange',
                    required: true,
                    useDropdown: true,
                    disabled: disabled || tempRangeDisabled,
                },
                {
                    name: 'opportunity',
                    componentName: 'OpportunitiesSelector',
                    labelKey: 'AccountLane.opportunity',
                    includeFilters: opportunityFilters,
                    disabled,
                },
                {
                    name: 'leaseType',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(LeaseType),
                    labelKey: 'AccountLane.leaseType',
                    required: true,
                    showInColumns: true,
                    disabled,
                },
                ...exists ? [{
                    name: 'aQuoteId',
                    componentName: 'SkyNetLink',
                    link: quoteInLink,
                    text: quoteInNumber,
                    type: TypeLink.EXTERNAL,
                    size: SizeLink.S,
                    labelKey: 'AccountLaneForm.quotedIn',
                    disabled,
                }] as NestedField<keyof AccountLaneFormFields>[] : [],
            ],
        },
        {
            id: 'column2',
            fields: [
                {
                    name: 'laneStatus',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(LaneStatus),
                    labelKey: 'LaneQuoteRequest.selectedLaneStatus',
                    disabled: true,
                },
                {
                    name: 'expPalletsYear',
                    required: true,
                    componentName: 'InputSingleline',
                    labelKey: 'LaneBasicView.expPalletsYear',
                    disabled,
                },
                {
                    name: 'originAirport',
                    componentName: 'LocationObjectSelector',
                    filterLocationType: LocationType.AIRPORT,
                    labelKey: 'AccountLaneForm.originAirport',
                    required: true,
                    disabled,
                },
                {
                    name: 'destinationAirport',
                    componentName: 'LocationObjectSelector',
                    filterLocationType: LocationType.AIRPORT,
                    labelKey: 'AccountLaneForm.destinationAirport',
                    required: true,
                    disabled,
                },
                {
                    name: 'laneLead',
                    componentName: 'UserObjectSelector',
                    labelKey: 'AccountLane.laneLead',
                    required: true,
                    disabled: disabled || !laneLeadPermission,
                },
                {
                    name: 'laneSupporter',
                    componentName: 'UserObjectSelector',
                    labelKey: 'AccountLane.laneSupporter',
                    disabled: disabled || !laneLeadPermission,
                },
            ],
        },
        {
            id: 'column3',
            fields: [
                {
                    name: 'customerSite',
                    componentName: 'LocationObjectSelector',
                    labelKey: 'LaneUpdateNewDto.customerSite',
                    disabled,
                },
                {
                    name: 'billingCompany',
                    componentName: 'BillingCompanyObjectSelector',
                    labelKey: 'LaneUpdateNewDto.billingCompany',
                    required: requiredBillingCompany,
                    disabled,
                },
                {
                    name: 'customerExecution',
                    componentName: 'LocationObjectSelector',
                    labelKey: 'LaneQuoteRequest.selectedCustomerExecution',
                    filterLocationType: LocationType.ADDRESS,
                    filterAddressCategory: [
                        AddressCategory.CUSTOMER,
                        AddressCategory.CUSTOMER_QUOTATION,
                    ],
                    disabled,
                },
                {
                    name: 'customerExecutionProvisional',
                    componentName: 'InputSingleline',
                    labelKey: 'AccountLane.customerExecutionProvisional',
                    disabled,
                },
                {
                    name: 'customerExecutionContacts',
                    componentName: 'MultipleContactObjectSelector',
                    labelKey: 'AccountLane.customerExecutionContacts',
                    disabled,
                },
            ],
        },
        {
            id: 'column4',
            fields: [
                {
                    name: 'consigneeProvisional',
                    componentName: 'InputSingleline',
                    labelKey: 'AccountLane.consigneeProvisional',
                    disabled,
                },
                {
                    name: 'consigneeContacts',
                    componentName: 'MultipleContactObjectSelector',
                    labelKey: 'AccountLane.consigneeContacts',
                    disabled,
                },
                {
                    name: 'laneComment',
                    componentName: 'LongText',
                    labelKey: 'AccountLane.laneComment',
                    disabled,
                },
                {
                    name: 'createdOn',
                    componentName: 'DateTimeSelect',
                    disabled: true,
                    labelKey: 'Accounts.createdOn',
                },
                {
                    name: 'createdBy',
                    componentName: 'InputSingleline',
                    disabled: true,
                    labelKey: 'Accounts.createdBy',
                },
            ],
        },
    ]

    const transportModeRoad = {
        name: 'transportModeRoad',
        labelKey: 'LaneGroupCreateOrDuplicateDto.transportModeRoad',
        componentName: 'EnumSelector',
        allowedValues: getAllowedValues(TransportModeRoad),
        showInColumns: true,
        required: true,
        disabled,
    } as FieldInCard<keyof AccountLaneFormFields>

    const isFtlPositioningTemperatureControlled = {
        name: 'isFtlPositioningTemperatureControlled',
        labelKey: 'AccountLane.isFtlPositioningTemperatureControlled',
        componentName: 'Checkbox',
        disabled,
    } as FieldInCard<keyof AccountLaneFormFields>

    const isLtlPositioningTemperatureControlled = {
        name: 'isLtlPositioningTemperatureControlled',
        labelKey: 'AccountLane.isLtlPositioningTemperatureControlled',
        componentName: 'Checkbox',
        disabled,
    } as FieldInCard<keyof AccountLaneFormFields>

    let transportModeFields = [transportModeRoad]

    if (showLTL) {
        transportModeFields = [
            ...transportModeFields,
            isLtlPositioningTemperatureControlled,
        ]
    }

    if (showFTL) {
        transportModeFields = [
            ...transportModeFields,
            isFtlPositioningTemperatureControlled,
        ]
    }

    const productTypesFields: FieldInCard<keyof AccountLaneFormFields>[] = [{
        id: 'column1',
        fields: [{
            name: 'requestedProductTypes',
            componentName: 'EnumChipSelector',
            allowedValues: getAllowedValues(ProductType),
            labelKey: 'AccountLane.requestedProductTypes',
            disabled: true,
        }],
    }]

    if (showAwardedProductTypes) {
        productTypesFields.push({
            id: 'column2',
            fields: [{
                name: 'awardedProductTypes',
                componentName: 'EnumChipSelector',
                allowedValues: getAllowedValues(ProductType),
                labelKey: 'AccountLane.awardedProductTypes',
                disabled: true,
            }],
        })
    }

    const handoverFields: FieldInCard<keyof AccountLaneFormFields>[] = [
        {
            id: 'column1',
            fields: [
                {
                    name: 'deliveryServiceType',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(DeliveryServiceType),
                    labelKey: 'AccountLane.deliveryServiceType',
                    notificationIconProps: {
                        content: TooltipContentDeliveryService,
                        type: TypeIcon.INFO,
                    },
                    required: true,
                    disabled,
                },
                ...(isDeliveryRoad
                    ? transportModeFields
                    : []) as NestedField<keyof AccountLaneFormFields>[],
            ],
        },
        {
            id: 'column2',
            fields: [
                ...(isDeliveryRoad ? [
                    {
                        name: 'handoverPointCountry',
                        componentName: 'CountryDataSelector',
                        labelKey: 'AccountLane.handoverPointCountry',
                        required: true,
                        disabled,
                    },
                    {
                        name: 'handoverPointZipCode',
                        componentName: 'ZipCodeInput',
                        labelKey: 'AccountLane.handoverPointZipCode',
                        required: true,
                        country: handoverPointCountry,
                        disabled,
                    },
                    {
                        name: 'handoverPointAddress',
                        componentName: 'InputSingleline',
                        labelKey: 'AccountLane.handoverPointAddress',
                        disabled,
                    },
                ] as NestedField<keyof AccountLaneFormFields>[] : []),
                {
                    name: 'handoverPointContacts',
                    componentName: 'MultipleContactObjectSelector',
                    labelKey: 'AccountLane.handoverPointContacts',
                    disabled,
                },
            ],
        },
    ]

    const collectionFields: FieldInCard<keyof AccountLaneFormFields>[] = [
        {
            id: 'column1',
            fields: [{
                name: 'collectionServiceType',
                componentName: 'EnumSelector',
                allowedValues: getAllowedValues(CollectionServiceType),
                labelKey: 'AccountLane.collectionServiceType',
                notificationIconProps: {
                    content: TooltipContentCollectionService,
                    type: TypeIcon.INFO,
                },
                required: true,
                disabled,
            }],
        },
        {
            id: 'column2',
            fields: [
                ...(isCollection ? [
                    {
                        name: 'collectionDropoffPointCountry',
                        componentName: 'CountryDataSelector',
                        labelKey: 'AccountLane.collectionDropoffPointCountry',
                        required: true,
                        disabled,
                    },
                    {
                        name: 'collectionDropoffPointZipCode',
                        componentName: 'ZipCodeInput',
                        labelKey: 'AccountLane.collectionDropoffPointZipCode',
                        required: true,
                        country: collectionDropoffPointCountry,
                        disabled,
                    },
                    {
                        name: 'collectionDropoffPointAddress',
                        componentName: 'InputSingleline',
                        labelKey: 'AccountLane.collectionDropoffPointAddress',
                        disabled,
                    },
                ] as NestedField<keyof AccountLaneFormFields>[] : []),
                {
                    name: 'collectionDropoffPointContacts',
                    componentName: 'MultipleContactObjectSelector',
                    labelKey: 'AccountLane.collectionDropoffPointContacts',
                    disabled,
                },
            ],
        },
    ]

    const defaultSections = [
        {
            id: 'general',
            title: 'General',
            className: 'general',
            fields: generalFields,
        },
        {
            id: 'handover',
            title: 'Handover',
            className: 'handover',
            fields: handoverFields,
        },
        {
            id: 'collection',
            title: 'Collection',
            className: 'collection',
            fields: collectionFields,
        },
    ]

    return showProductTypes ? [
        ...defaultSections,
        {
            id: 'productTypes',
            title: 'Product Types',
            className: 'productTypes',
            fields: productTypesFields,
        },
    ] : defaultSections
}

export default getFields
